import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Form, Col, Row, InputGroup, Table, Spinner } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { format } from 'date-fns';
import { FaCalendarAlt } from 'react-icons/fa';
import Select from 'react-select';

import Swal from 'sweetalert2';

// --- Theme Configuration ---
const themeColor = '#004aad';
const themeColorDarker = '#003a8c';

// API Base URL - Change this to your actual backend URL
const API_BASE_URL = 'https://hms.backend.hiragroupcloud.com'; // Changed from 5002 to 5002

// --- Reusable Styles ---
const styles = {
  modalHeader: { backgroundColor: themeColor, color: '#fff', borderBottom: 'none' },
  modalTitle: { fontWeight: 'bold' },
  modalFooter: { borderTop: 'none', paddingTop: '0.5rem', paddingBottom: '1rem' },
  modalBody: { padding: '1.5rem', maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' },
  sectionTitle: { color: themeColor, fontSize: '1.1rem', marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: '1px solid #dee2e6', fontWeight: 500 },
  requiredAsterisk: { color: 'red', marginLeft: '3px' },
  buttonSave: { backgroundColor: themeColor, borderColor: themeColor, color: '#fff', minWidth: '80px', fontWeight: 500 },
  buttonSaveHover: { backgroundColor: themeColorDarker, borderColor: themeColorDarker },
  formLabel: { fontWeight: 'bold', marginBottom: '0.3rem', display: 'block'},
  formInput: { fontSize: '1rem', width: '100%' },
  datePickerInput: { backgroundColor: '#fff', cursor: 'pointer', width: '100%' },
  inputGroupText: { backgroundColor: '#fff', borderLeft: 'none' },
  calendarIcon: { color: themeColor },
  totalsCol: { paddingTop: '1.5rem' },
  totalsRow: { marginBottom: '0.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  totalsLabel: { fontWeight: 'bold', color: '#555', textAlign: 'right', paddingRight: '1rem', minWidth: '80px'},
  totalsValue: { textAlign: 'right', color: '#212529', fontSize: '1rem' },
  tableContainer: { maxHeight: '350px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '4px' },
};

// Credit Type Options
const creditTypeOptions = [
  { value: 'refund', label: 'Refund' },
  { value: 'adjustment', label: 'Adjustment' },
  { value: 'goodwill', label: 'Goodwill' },
  { value: 'other', label: 'Other' },
  { value: 'alloy', label: 'Alloy' },
  { value: 'bad_debt_write_off', label: 'Bad Debt Write-Off' },
  { value: 'ceramic', label: 'Ceramic' },
  { value: 'crown_bridge', label: 'Crown & Bridge' },
  { value: 'denture', label: 'Denture' },
  { value: 'general', label: 'General' },
  { value: 'interest_reversal', label: 'Interest Reversal' },
  { value: 'models', label: 'Models' },
  { value: 'prompt_payment', label: 'Prompt Payment' },
  { value: 'volume_discount', label: 'Volume Discount' }
];

function CreditMemo({ show, handleClose }) {


  // User identification
  const [userLabId, setUserLabId] = useState('');
  const [backendError, setBackendError] = useState(false);

  // --- State Variables ---
  const [creditDate, setCreditDate] = useState(new Date());
  const [selectedAccount, setSelectedAccount] = useState('');
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [accountName, setAccountName] = useState('');
  const [amount, setAmount] = useState('');
  const [referenceNo, setReferenceNo] = useState('');
  const [creditType, setCreditType] = useState('');
  const [memo, setMemo] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [error, setError] = useState(null);
  const [formSubmitted, setFormSubmitted] = useState(false);

  // State for Outstanding Invoices
  const [tableData, setTableData] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [invoiceError, setInvoiceError] = useState(null);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [invoicePayments, setInvoicePayments] = useState({});

  // Calculated amounts
  const [appliedAmount, setAppliedAmount] = useState(0);
  const [unappliedAmount, setUnappliedAmount] = useState(0);

  // Ref to ignore auto-apply when updating amount from selection
  const ignoreAutoApplyRef = useRef(false);

  // Load userLabId from localStorage when modal opens
  useEffect(() => {
    if (show) {
      const storedUserLabId = localStorage.getItem('userLabId');
      if (storedUserLabId) {
        const cleanedId = String(storedUserLabId).trim();
        setUserLabId(cleanedId);
        setBackendError(false);
      } else {
        setError('User identification not found. Please log in again.');
      }
    }
  }, [show]);

  // Custom styles for react-select to match Bootstrap and handle long labels with ellipsis
  const customStyles = {
    control: (provided) => ({
      ...provided,
      backgroundColor: '#fff',
      border: '1px solid #ced4da',
      borderRadius: '.25rem',
      minHeight: 'calc(1.5em + .75rem + 2px)',
      boxShadow: 'none',
      '&:hover': {
        border: '1px solid #ced4da',
      },
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
      borderRadius: '.25rem',
      border: '1px solid #ced4da',
      boxShadow: '0 0.5rem 1rem rgba(0,0,0,.15)',
    }),
    option: (provided, state) => ({
      ...provided,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      backgroundColor: state.isSelected ? themeColor : 'white',
      color: state.isSelected ? 'white' : 'black',
      '&:hover': {
        backgroundColor: state.isSelected ? themeColor : '#f8f9fa',
        color: state.isSelected ? 'white' : 'black',
      },
    }),
    singleValue: (provided) => ({
      ...provided,
      color: '#212529',
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#6c757d',
    }),
  };

  // --- Data Fetching - Fetch accounts for the current lab ---
  useEffect(() => {
    const fetchAccounts = async () => {
      setLoadingAccounts(true);
      setError(null);

      if (!userLabId) {
        setLoadingAccounts(false);
        setAccounts([]);
        return;
      }

      try {
        console.log('Fetching accounts for lab:', userLabId);

        const response = await fetch(`${API_BASE_URL}/api/account/lab-accounts/${userLabId}`);

        if (!response.ok) {
          if (response.status === 404) {
            setAccounts([]);
            setError('No accounts found for this lab.');
          } else if (response.status === 0 || response.status === -1) {
            throw new Error('Cannot connect to server. Please check if the backend is running.');
          } else {
            throw new Error(`Failed to fetch accounts: ${response.status}`);
          }
        } else {
          const result = await response.json();

          if (result.success && Array.isArray(result.data)) {
            const sortedAccounts = [...result.data].sort((a, b) => {
              const nameA = a.account_name.toLowerCase();
              const nameB = b.account_name.toLowerCase();
              if (nameA < nameB) return -1;
              if (nameA > nameB) return 1;
              return 0;
            });
            setAccounts(sortedAccounts);
            setBackendError(false);
          } else if (result.success && result.message === 'No accounts found for this lab.') {
            setAccounts([]);
          } else {
            throw new Error('Invalid data format');
          }
        }
      } catch (fetchError) {
        console.error("Fetch Accounts Error:", fetchError);
        setBackendError(true);
        setError('Failed to connect to server. Please ensure the backend server is running on port 5002.');
        setAccounts([]);
      } finally {
        setLoadingAccounts(false);
      }
    };

    if (show && userLabId) {
      fetchAccounts();
    } else if (show && !userLabId) {
      setLoadingAccounts(false);
      setAccounts([]);
    } else if (!show) {
      resetFormState();
    }
  }, [show, userLabId]);

  // Update selectedAccountId and accountName when selectedAccount changes
  useEffect(() => {
    if (selectedAccount) {
      const account = accounts.find(acc => acc._id === selectedAccount);
      setSelectedAccountId(account ? account.accountId : '');
      setAccountName(account ? account.account_name : '');
    } else {
      setSelectedAccountId('');
      setAccountName('');
      setSelectedInvoices([]);
      setReferenceNo('');
      setAmount('');
      setTableData([]);
      setInvoicePayments({});
      setAppliedAmount(0);
      setUnappliedAmount(0);
    }
  }, [selectedAccount, accounts]);

  // Helper function to parse invoice number for sorting
  const parseInvoiceNumber = (invoiceNumber) => {
    if (!invoiceNumber) return { prefix: '', number: 0 };

    const match = invoiceNumber.toString().match(/(\D*)(\d+)/);
    if (match) {
      return {
        prefix: match[1] || '',
        number: parseInt(match[2], 10) || 0
      };
    }

    return { prefix: invoiceNumber.toString(), number: 0 };
  };

  // Fetch Invoices when Account Changes
  useEffect(() => {
    const fetchInvoices = async (accountId) => {
      if (!accountId) {
        setTableData([]);
        setSelectedInvoices([]);
        setReferenceNo('');
        setAmount('');
        setAppliedAmount(0);
        setUnappliedAmount(0);
        return;
      }

      setLoadingInvoices(true);
      setInvoiceError(null);

      try {
        console.log('Fetching invoices for account:', accountId);

        const response = await fetch(`${API_BASE_URL}/api/invoices/invoices/account?accountId=${accountId}`);

        if (!response.ok) {
          if (response.status === 0 || response.status === -1) {
            throw new Error('Cannot connect to server');
          }
          throw new Error(`Failed to fetch invoices: ${response.status}`);
        }

        let invoices = await response.json();

        // Format invoices
        const formattedInvoices = invoices.map(invoice => {
          const remaining = Math.max(0, (invoice.total || 0) - (invoice.paid || 0));
          const parsedInvoiceNumber = parseInvoiceNumber(invoice.invoiceNumber);

          return {
            id: invoice._id || invoice.invoiceNumber,
            invoiceNumber: invoice.invoiceNumber,
            description: `Invoice ${invoice.invoiceNumber}`,
            invoiceDate: format(new Date(invoice.invoiceDate), 'yyyy-MM-dd'),
            invoiceDateObj: new Date(invoice.invoiceDate),
            total: invoice.total || 0,
            paid: invoice.paid || 0,
            originalRemaining: remaining,
            remaining: remaining,
            payment: 0,
            parsedInvoiceNumber: parsedInvoiceNumber,
          };
        });

        // Only show invoices with remaining balance
        let filteredInvoices = formattedInvoices.filter(inv => inv.remaining > 0);

        // Sort invoices by invoice number (ascending order - smallest to largest)
        filteredInvoices.sort((a, b) => {
          const aParsed = a.parsedInvoiceNumber;
          const bParsed = b.parsedInvoiceNumber;

          if (aParsed.prefix < bParsed.prefix) return -1;
          if (aParsed.prefix > bParsed.prefix) return 1;

          return aParsed.number - bParsed.number;
        });

        // Remove parsedInvoiceNumber property before setting state
        const finalInvoices = filteredInvoices.map(({ parsedInvoiceNumber, ...rest }) => rest);

        // Preserve existing selections and payment amounts for invoices that still exist
        const updatedTableData = finalInvoices.map(invoice => {
          const existingInvoice = tableData.find(row => row.id === invoice.id);
          if (existingInvoice && selectedInvoices.includes(invoice.id)) {
            return {
              ...invoice,
              payment: existingInvoice.payment,
              remaining: Math.max(0, invoice.originalRemaining - (existingInvoice.payment || 0)),
            };
          }
          return invoice;
        });

        setTableData(updatedTableData);

        // Update selected invoices to only include those that still exist
        const updatedSelectedInvoices = selectedInvoices.filter(id =>
          updatedTableData.some(row => row.id === id)
        );
        setSelectedInvoices(updatedSelectedInvoices);

      } catch (err) {
        console.error("Fetch Invoices Error:", err);
        setInvoiceError('Error fetching invoices: ' + err.message);
        setTableData([]);
        setSelectedInvoices([]);
        setReferenceNo('');
        setAmount('');
        setAppliedAmount(0);
        setUnappliedAmount(0);
      } finally {
        setLoadingInvoices(false);
      }
    };

    if (userLabId && !backendError) {
      fetchInvoices(selectedAccountId);
    }
  }, [selectedAccountId, userLabId, backendError]);

  // Update referenceNo when selected invoices change
  useEffect(() => {
    const selectedRows = tableData.filter(row => selectedInvoices.includes(row.id));
    const invoiceNumbers = selectedRows.map(row => row.invoiceNumber);
    setReferenceNo(invoiceNumbers.join(', '));
  }, [selectedInvoices, tableData]);

  // Update applied and unapplied amounts when tableData payments change
  useEffect(() => {
    const totalApplied = tableData.reduce((sum, row) => sum + parseFloat(row.payment || 0), 0);
    setAppliedAmount(totalApplied);

    const numericAmount = parseFloat(amount || 0);
    if (!isNaN(numericAmount)) {
      const unapplied = numericAmount - totalApplied;
      setUnappliedAmount(unapplied > 0 ? unapplied : 0);

      if (unapplied > 0) {
        console.log(`Unapplied credit: ${unapplied}`);
      }
    } else {
      setUnappliedAmount(0);
    }
  }, [amount, tableData]);

  // Calculate total amount when invoices are selected/deselected
  useEffect(() => {
    const calculateTotalFromSelectedInvoices = () => {
      const totalPayment = selectedInvoices.reduce((sum, invoiceId) => {
        const invoice = tableData.find(row => row.id === invoiceId);
        return sum + (invoice ? parseFloat(invoice.payment || 0) : 0);
      }, 0);

      return totalPayment;
    };

    if (selectedInvoices.length > 0) {
      const totalPayment = calculateTotalFromSelectedInvoices();
      ignoreAutoApplyRef.current = true;
      setAmount(totalPayment > 0 ? totalPayment.toString() : '');
    } else {
      if (selectedInvoices.length === 0 && !selectedAccountId) {
        setAmount('');
      }
    }
  }, [selectedInvoices, tableData]);

  // Auto-select invoices based on entered amount (like payment component)
  useEffect(() => {
    if (ignoreAutoApplyRef.current) {
      ignoreAutoApplyRef.current = false;
      return;
    }

    if (amount && !isNaN(parseFloat(amount)) && selectedAccountId && tableData.length > 0 && userLabId && !backendError) {
      const enteredAmount = parseFloat(amount);
      let remainingAmount = enteredAmount;
      const newSelectedInvoices = [];

      // Create a copy of table data for calculation
      const updatedTableData = tableData.map(row => {
        return {
          ...row,
          payment: 0,
          remaining: row.originalRemaining
        };
      });

      // Sort invoices by date (oldest first) and then by invoice number
      const sortedInvoices = [...updatedTableData].sort((a, b) => {
        const dateA = new Date(a.invoiceDate);
        const dateB = new Date(b.invoiceDate);
        if (dateA < dateB) return -1;
        if (dateA > dateB) return 1;

        return a.invoiceNumber.localeCompare(b.invoiceNumber);
      });

      // Apply credits to invoices until amount is exhausted
      const finalTableData = sortedInvoices.map(row => {
        if (remainingAmount <= 0) return row;

        if (row.originalRemaining > 0) {
          if (remainingAmount >= row.originalRemaining) {
            // Full credit for this invoice
            newSelectedInvoices.push(row.id);
            remainingAmount -= row.originalRemaining;
            return {
              ...row,
              payment: row.originalRemaining,
              remaining: 0
            };
          } else {
            // Partial credit for this invoice
            newSelectedInvoices.push(row.id);
            const paymentAmount = remainingAmount;
            remainingAmount = 0;
            return {
              ...row,
              payment: paymentAmount,
              remaining: row.originalRemaining - paymentAmount
            };
          }
        }
        return row;
      });

      // Update state with new selections
      setSelectedInvoices(newSelectedInvoices);
      setTableData(finalTableData);

    } else if (!amount) {
      // If amount is cleared, clear all selections
      setSelectedInvoices([]);
      const resetTableData = tableData.map(row => ({
        ...row,
        payment: 0,
        remaining: row.originalRemaining
      }));
      setTableData(resetTableData);
    }
  }, [amount, selectedAccountId, userLabId, backendError]);

  // --- Helper Functions ---
  const resetFormState = () => {
    setCreditDate(new Date());
    setSelectedAccount('');
    setSelectedAccountId('');
    setAccountName('');
    setAmount('');
    setReferenceNo('');
    setCreditType('');
    setMemo('');
    setFormSubmitted(false);
    setError(null);
    setTableData([]);
    setLoadingInvoices(false);
    setInvoiceError(null);
    setSelectedInvoices([]);
    setInvoicePayments({});
    setAppliedAmount(0);
    setUnappliedAmount(0);
    setBackendError(false);
  };

  const handleCloseModal = () => {
    resetFormState();
    handleClose();
  };

  // --- Event Handlers ---
  const handleAccountChange = (selectedOption) => {
    const value = selectedOption ? selectedOption.value : '';
    const label = selectedOption ? selectedOption.label : '';
    setSelectedAccount(value);
    setAccountName(label);
  };

  const handleAmountChange = (e) => {
    const value = e.target.value;
    setAmount(value);
  };

  const handleRefNoChange = (e) => setReferenceNo(e.target.value);
  const handleCreditTypeChange = (e) => setCreditType(e.target.value);
  const handleMemoChange = (e) => setMemo(e.target.value);
  const handleDateChange = (date) => setCreditDate(date);

  const handleInvoiceSelection = (invoiceId) => {
    const isSelected = selectedInvoices.includes(invoiceId);

    if (isSelected) {
      setSelectedInvoices(prev => prev.filter(id => id !== invoiceId));

      const updatedTableData = tableData.map(row => {
        if (row.id === invoiceId) {
          return {
            ...row,
            payment: 0,
            remaining: row.originalRemaining,
          };
        }
        return row;
      });

      setTableData(updatedTableData);
    } else {
      setSelectedInvoices(prev => [...prev, invoiceId]);

      const updatedTableData = tableData.map(row => {
        if (row.id === invoiceId) {
          return {
            ...row,
            payment: row.originalRemaining,
            remaining: 0,
          };
        }
        return row;
      });

      setTableData(updatedTableData);
    }
  };

  const handleInvoicePaymentChange = (index, paymentValue) => {
    const updatedTableData = [...tableData];
    const row = updatedTableData[index];
    const newPayment = parseFloat(paymentValue || 0);

    if (row) {
      const maxAllowedPayment = row.originalRemaining;
      const appliedPayment = Math.min(newPayment, maxAllowedPayment);
      updatedTableData[index] = {
        ...row,
        payment: appliedPayment,
        remaining: row.originalRemaining - appliedPayment,
      };
      setTableData(updatedTableData);
    }
  };

  const handleSelectAll = () => {
    if (tableData.length === 0) return;

    const allInvoiceIds = tableData.map(row => row.id);
    setSelectedInvoices(allInvoiceIds);

    const updatedTableData = tableData.map(row => ({
      ...row,
      payment: row.originalRemaining,
      remaining: 0,
    }));

    setTableData(updatedTableData);
  };

  const handleClearAll = () => {
    setSelectedInvoices([]);

    const updatedTableData = tableData.map(row => ({
      ...row,
      payment: 0,
      remaining: row.originalRemaining,
    }));

    setTableData(updatedTableData);
    setAmount('');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setFormSubmitted(true);

    if (!userLabId) {
      Swal.fire({
        title: 'Error!',
        text: 'Unable to process credit: User identification not found. Please log in again.',
        icon: 'error',
        confirmButtonColor: themeColor
      });
      return;
    }

    // Validation
    if (!selectedAccount || !amount || !creditDate || !creditType) {
      Swal.fire({
        title: 'Validation Error',
        text: 'Please fill in all required fields (*)',
        icon: 'warning',
        confirmButtonColor: themeColor
      });
      return;
    }

    const numericAmount = parseFloat(amount || 0);
    if (isNaN(numericAmount) || numericAmount <= 0) {
      Swal.fire({
        title: 'Invalid Amount',
        text: 'Please enter a valid positive amount.',
        icon: 'warning',
        confirmButtonColor: themeColor
      });
      return;
    }

    // Check if at least one invoice is selected
    if (selectedInvoices.length === 0) {
      Swal.fire({
        title: 'Validation Error',
        text: 'Please select at least one invoice to apply the credit.',
        icon: 'warning',
        confirmButtonColor: themeColor
      });
      return;
    }

    const appliedInvoices = tableData
      .filter(row => selectedInvoices.includes(row.id) && parseFloat(row.payment || 0) > 0)
      .map(row => {
        const existingPaid = parseFloat(row.paid || 0);
        const newPayment = parseFloat(row.payment || 0);
        const newPaidAmount = existingPaid + newPayment;

        return {
          invoiceNumber: row.invoiceNumber,
          appliedAmount: newPayment,
          newPaidAmount: newPaidAmount
        };
      });

    if (appliedInvoices.length === 0) {
      Swal.fire({
        title: 'Validation Error',
        text: 'Please enter credit amounts for selected invoices.',
        icon: 'warning',
        confirmButtonColor: themeColor
      });
      return;
    }

    // Get invoice numbers as an array
    const referenceNoArray = selectedInvoices
      .map(id => tableData.find(row => row.id === id)?.invoiceNumber)
      .filter(Boolean);

    // Convert array to string for mongoose schema
    const referenceNoString = referenceNoArray.join(', ');

    const creditData = {
      accountId: selectedAccountId,
      accountName,
      amount: numericAmount,
      creditDate: format(creditDate, 'yyyy-MM-dd'),
      creditType,
      referenceNo: referenceNoString,
      memo,
      appliedInvoices,
      finalAppliedAmount: appliedAmount,
      finalUnappliedAmount: unappliedAmount,
      addedBy: userLabId,
      uniqueCreditId: `CRD-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    };

    console.log('Sending credit data:', creditData);

    try {
      const response = await fetch(`${API_BASE_URL}/api/credit-memos/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(creditData)
      });

      const responseText = await response.text();
      let responseData;

      try {
        responseData = JSON.parse(responseText);
      } catch (e) {
        throw new Error(`Invalid JSON response: ${responseText}`);
      }

      if (!response.ok) {
        throw new Error(responseData.message || `Failed to create credit memo: ${response.status}`);
      }

      if (responseData.success) {
        const updatedTableData = tableData.map(row => {
          const appliedInvoice = appliedInvoices.find(inv => inv.invoiceNumber === row.invoiceNumber);
          if (appliedInvoice) {
            return {
              ...row,
              paid: appliedInvoice.newPaidAmount,
              remaining: row.total - appliedInvoice.newPaidAmount,
              payment: 0,
              originalRemaining: Math.max(0, row.total - appliedInvoice.newPaidAmount),
            };
          }
          return row;
        });

        setTableData(updatedTableData);
        setSelectedInvoices([]);
        setReferenceNo('');
        setAmount('');
        setAppliedAmount(0);
        setUnappliedAmount(0);
        setMemo('');

        Swal.fire({
          title: 'Success!',
          text: responseData.message || 'Credit memo created successfully!',
          icon: 'success',
          confirmButtonColor: themeColor
        });

        handleCloseModal();
      } else {
        throw new Error(responseData.message || 'Failed to create credit memo');
      }
    } catch (error) {
      console.error('Error creating credit memo:', error);
      Swal.fire({
        title: 'Error!',
        text: 'Failed to create credit memo: ' + error.message,
        icon: 'error',
        confirmButtonColor: themeColor
      });
    }
  };

  // --- Custom Input Component ---
  const CustomDateInput = React.forwardRef(({ value, onClick, name }, ref) => (
    <Form.Control
      type="text"
      value={value}
      onClick={onClick}
      ref={ref}
      readOnly
      name={name}
      style={{...styles.formInput, ...styles.datePickerInput}}
    />
  ));

  const allSelected = tableData.length > 0 && selectedInvoices.length === tableData.length;

  // --- Render Logic ---
  return (
    <Modal show={show} onHide={handleCloseModal} size="lg" backdrop="static" keyboard={false}>
      <Modal.Header closeButton style={styles.modalHeader}>
        <Modal.Title style={styles.modalTitle}>Create New Credit</Modal.Title>
      </Modal.Header>
      <Modal.Body style={styles.modalBody}>
        {error && <div className="alert alert-danger py-2">{error}</div>}

        {backendError && (
          <div className="alert alert-warning">
            <strong>Connection Issue:</strong> Unable to connect to the server. Please ensure the backend server is running on port 5002.
          </div>
        )}

        {!userLabId && show && (
          <div className="alert alert-warning">
            <strong>Warning:</strong> User not identified. Please log in again.
          </div>
        )}

        <Form noValidate onSubmit={handleSave}>
          {/* --- Credit Details --- */}
          <h4 style={styles.sectionTitle}>Credit Details</h4>

          <Row className="mb-3">
            <Col md={5} className="mb-3 mb-md-0">
              <Form.Group controlId="creditAccount">
                <Form.Label style={styles.formLabel}>
                  Account<span style={styles.requiredAsterisk}>*</span>
                </Form.Label>
                <Select
                  options={accounts.map((account) => ({
                    value: account._id,
                    label: account.account_name
                  }))}
                  value={selectedAccount ? {
                    value: selectedAccount,
                    label: accountName
                  } : null}
                  onChange={handleAccountChange}
                  placeholder={loadingAccounts ? 'Loading accounts...' : !userLabId ? 'Please log in to view accounts' : accounts.length === 0 ? 'No accounts found for your lab' : 'Select Account'}
                  isDisabled={loadingAccounts || !userLabId || backendError}
                  isClearable={true}
                  styles={customStyles}
                  required
                />
                {userLabId && accounts.length === 0 && !loadingAccounts && !backendError && (
                  <Form.Text className="text-muted">
                    No accounts found for your lab. Please create an account first.
                  </Form.Text>
                )}
                <Form.Control.Feedback type="invalid">
                  Please select an account.
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={5}>
              <Form.Group controlId="creditAmount" className="mb-3">
                <Form.Label style={styles.formLabel}>
                  Amount <span style={styles.requiredAsterisk}>*</span>
                </Form.Label>
                <Form.Control
                  type="number"
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                  name="amount"
                  value={amount}
                  onChange={handleAmountChange}
                  required
                  style={styles.formInput}
                  isInvalid={formSubmitted && (!amount || parseFloat(amount) <= 0)}
                  disabled={!userLabId || backendError}
                />
                <Form.Control.Feedback type="invalid">
                  Please enter a valid positive amount.
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={5} className="mb-3 mb-md-0">
              <Form.Group controlId="creditDate">
                <Form.Label style={styles.formLabel}>
                  Credit Date<span style={styles.requiredAsterisk}>*</span>
                </Form.Label>
                <InputGroup>
                  <DatePicker
                    selected={creditDate}
                    onChange={handleDateChange}
                    dateFormat="d MMM yyyy"
                    customInput={<CustomDateInput name="creditDate" />}
                    popperPlacement="bottom-start"
                    required
                    disabled={!userLabId || backendError}
                  />
                  <InputGroup.Text style={styles.inputGroupText}>
                    <FaCalendarAlt style={styles.calendarIcon} />
                  </InputGroup.Text>
                </InputGroup>
                {formSubmitted && !creditDate && (
                  <div className="invalid-feedback d-block">Please select a date.</div>
                )}
              </Form.Group>
            </Col>

            <Col md={5}>
              <Form.Group controlId="creditReferenceNo">
                <Form.Label style={styles.formLabel}>Reference No.</Form.Label>
                <Form.Control
                  type="text"
                  name="referenceNo"
                  value={referenceNo}
                  onChange={handleRefNoChange}
                  style={styles.formInput}
                  readOnly
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-4">
            <Col md={5}>
              <Form.Group controlId="creditType">
                <Form.Label style={styles.formLabel}>
                  Type<span style={styles.requiredAsterisk}>*</span>
                </Form.Label>
                <Form.Select
                  name="creditType"
                  value={creditType}
                  onChange={handleCreditTypeChange}
                  required
                  style={styles.formInput}
                  isInvalid={formSubmitted && !creditType}
                  disabled={!userLabId || backendError}
                >
                  <option value="">Select Credit Type</option>
                  {creditTypeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  Please select a credit type.
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          {/* --- Apply to Outstanding Invoices Section --- */}
          {selectedAccountId && userLabId && !backendError && (
            <>
              <h4 style={{...styles.sectionTitle, marginTop: '2rem'}}>Apply to Outstanding Invoices</h4>
              {/* Conditional Rendering for Invoice Table */}
              {loadingInvoices && (
                <div className="text-center my-3">
                  <Spinner animation="border" role="status" variant="primary">
                    <span className="visually-hidden">Loading invoices...</span>
                  </Spinner>
                </div>
              )}
              {!loadingInvoices && invoiceError && (
                <div className="alert alert-warning py-2">{invoiceError}</div>
              )}
              {!loadingInvoices && !invoiceError && tableData.length === 0 && (
                <p className="text-muted fst-italic my-3">No outstanding invoices found for this account.</p>
              )}
              {!loadingInvoices && !invoiceError && tableData.length > 0 && (
                <>
                  <div style={styles.tableContainer}>
                    <Table striped bordered hover responsive className="mt-3 mb-0">
                      <thead style={{ backgroundColor: '#f8f9fa', position: 'sticky', top: 0, zIndex: 1 }}>
                        <tr>
                          <th style={{ width: '50px' }}>
                            <Form.Check
                              type="checkbox"
                              checked={allSelected}
                              onChange={allSelected ? handleClearAll : handleSelectAll}
                              aria-label={allSelected ? "Deselect all invoices" : "Select all invoices"}
                              disabled={!userLabId || backendError}
                            />
                          </th>
                          <th>Description</th>
                          <th>Invoice Date</th>
                          <th className="text-end">Total</th>
                          <th className="text-end">Paid</th>
                          <th className="text-end">Remaining</th>
                          <th className="text-end" style={{ minWidth: '120px' }}>Credit Applied</th>
                         </tr>
                      </thead>
                      <tbody>
                        {tableData.map((row, index) => (
                          <tr key={row.id || index}>
                            <td>
                              <Form.Check
                                type="checkbox"
                                checked={selectedInvoices.includes(row.id)}
                                onChange={() => handleInvoiceSelection(row.id)}
                                aria-label={`Select invoice ${row.invoiceNumber}`}
                                disabled={!userLabId || backendError}
                              />
                            </td>
                            <td>{row.description}</td>
                            <td>{format(new Date(row.invoiceDate + 'T00:00:00'), 'd MMM yyyy')}</td>
                            <td className="text-end">{(Number(row.total || 0))}</td>
                            <td className="text-end">{(Number(row.paid || 0))}</td>
                            <td className="text-end">{(Number(row.remaining || 0))}</td>
                            <td className="text-end">
                              <Form.Control
                                type="number"
                                size="sm"
                                className="text-end"
                                placeholder="0.00"
                                step="0.01"
                                min="0"
                                max={Number(row.originalRemaining || 0)}
                                value={row.payment === 0 ? '' : row.payment}
                                onChange={(e) => handleInvoicePaymentChange(index, e.target.value)}
                                aria-label={`Credit for ${row.description}`}
                                disabled={!selectedInvoices.includes(row.id) || !userLabId || backendError}
                              />
                            </td>
                           </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </>
              )}
            </>
          )}

          {/* --- Memo / Applied/Unapplied Section --- */}
          <Row className="mt-4 pt-3 align-items-start">
            {/* Memo */}
            <Col md={7} className="mb-3 mb-md-0">
              <Form.Group controlId="creditMemo">
                <Form.Label style={styles.formLabel}>Memo</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={memo}
                  onChange={handleMemoChange}
                  style={styles.formInput}
                  disabled={!userLabId || backendError}
                />
              </Form.Group>
            </Col>

            {/* Totals */}
            <Col md={5} style={styles.totalsCol}>
              <div style={styles.totalsRow}>
                <span style={styles.totalsLabel}>Applied</span>
                <span style={styles.totalsValue}>{(appliedAmount)}</span>
              </div>
              <div style={styles.totalsRow}>
                <span style={styles.totalsLabel}>Unapplied</span>
                <span style={styles.totalsValue}>{(unappliedAmount)}</span>
              </div>
              {unappliedAmount > 0 && (
                <div className="mt-3 text-muted small">
                  Credit amount exceeding the outstanding invoices total creates a new unapplied credit
                </div>
              )}
            </Col>
          </Row>
        </Form>
      </Modal.Body>
      <Modal.Footer style={styles.modalFooter}>
        <Button variant="light" onClick={handleCloseModal} style={{ minWidth: '80px' }}>
          Cancel
        </Button>
        <Button
          type="submit"
          onClick={handleSave}
          style={{
            ...styles.buttonSave,
            ...((!userLabId || backendError) ? { opacity: 0.6, cursor: 'not-allowed' } : {})
          }}
          onMouseOver={e => {
            if (userLabId && !backendError) {
              e.currentTarget.style.backgroundColor = styles.buttonSaveHover.backgroundColor;
              e.currentTarget.style.borderColor = styles.buttonSaveHover.borderColor;
            }
          }}
          onMouseOut={e => {
            if (userLabId && !backendError) {
              e.currentTarget.style.backgroundColor = styles.buttonSave.backgroundColor;
              e.currentTarget.style.borderColor = styles.buttonSave.borderColor;
            }
          }}
          disabled={!userLabId || backendError}
        >
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default CreditMemo;